import type { OrderSetBookingDto } from "@repo/data-store";
import type { SessionBookingTarget } from "./types";

export interface SchedulerInput {
  patientId: string;
  caseId: string;
  sessions: SessionBookingTarget[];
}

export type SchedulerInputResult = { status: "ready"; data: SchedulerInput } | { status: "error"; message: string };

/** Resolve only after hydration. Caller order is the scheduling sequence. */
export function resolveSchedulerInput(
  orderSetIds: readonly string[],
  bookings: readonly OrderSetBookingDto[],
): SchedulerInputResult {
  const ids = [...new Set(orderSetIds)];
  if (ids.length === 0) return { status: "error", message: "Choose at least one session to schedule." };
  const byId = new Map(bookings.map((set) => [set.orderSetId, set]));
  const sets = ids.flatMap((id) => byId.get(id) ?? []);
  const first = sets[0];
  if (!first || sets.length !== ids.length)
    return { status: "error", message: "One or more sessions could not be found." };
  if (!first.caseId || sets.some((set) => !set.caseId)) {
    return { status: "error", message: "Each session must belong to a case before it can be scheduled." };
  }
  if (sets.some((set) => set.patientId !== first.patientId || set.caseId !== first.caseId)) {
    return { status: "error", message: "Schedule sessions for one patient and case at a time." };
  }
  if (sets.some((set) => !set.isBookable)) {
    return {
      status: "error",
      message:
        "One or more sessions are no longer available to schedule. Check their booking requirements and try again.",
    };
  }
  return {
    status: "ready",
    data: {
      patientId: first.patientId,
      caseId: first.caseId,
      sessions: sets.map((set) => ({
        orderSetId: set.orderSetId,
        sessionTitle: set.protocolTitle,
        duration: set.durationMinutes,
        procedures: set.steps
          .flatMap((step) => step.orders)
          .filter((order) => order.scheduling.isBookable)
          .map((order) => ({
            id: order.orderId,
            title: order.title,
            activityDefinitionCanonical: `${order.definition.url}|${order.definition.version}`,
          })),
      })),
    },
  };
}
